import React, { useState, useEffect } from 'react';
import { LoadingSpinner, SkeletonBlock } from '../../../components/SkeletonLoader';


// ─── Audit Log Utilities ───────────────────────────────────────────────────
export const AUDIT_CATEGORIES = {
  ACCOUNT: 'account',
  SUBSCRIPTION: 'subscription',
  VEHICLE: 'vehicle',
  PAYMENT: 'payment',
  PROFILE: 'profile',
};

export const AUDIT_STATUS = {
  SUCCESS: 'success',
  FAILED: 'failed',
  WARNING: 'warning',
};

const STATUS_CONFIG = {
  [AUDIT_STATUS?.SUCCESS]: {
    label: 'Vellykket',
    color: '#10B981',
    bg: 'rgba(16,185,129,0.12)',
    border: 'rgba(16,185,129,0.3)',
  },
  [AUDIT_STATUS?.FAILED]: {
    label: 'Mislyktes',
    color: '#EF4444',
    bg: 'rgba(239,68,68,0.12)',
    border: 'rgba(239,68,68,0.3)',
  },
  [AUDIT_STATUS?.WARNING]: {
    label: 'Advarsel',
    color: '#F59E0B',
    bg: 'rgba(245,158,11,0.12)',
    border: 'rgba(245,158,11,0.3)',
  },
};

const STORAGE_KEY = 'veiassistent_audit_log';

const DEMO_DEVICES = [
  { device: 'Chrome / Windows 11', ip: '192.168.1.42' },
  { device: 'Safari / macOS Ventura', ip: '10.0.0.15' },
  { device: 'Firefox / Ubuntu 22.04', ip: '172.16.0.8' },
  { device: 'Chrome / Android 14', ip: '192.168.2.101' },
  { device: 'Edge / Windows 10', ip: '10.10.5.33' },
];

export const addAuditEntry = (category, action, details, userId = null, status = AUDIT_STATUS?.SUCCESS, device = null, ip = null) => {
  try {
    const session = JSON.parse(localStorage.getItem('veiassistent_session') || 'null');
    const uid = userId || session?.id || 'unknown';
    const entries = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    if (!entries?.[uid]) entries[uid] = [];
    const randomDevice = DEMO_DEVICES?.[Math.floor(Math.random() * DEMO_DEVICES?.length)];
    const entry = {
      id: `audit-${Date.now()}-${Math.random()?.toString(36)?.slice(2, 7)}`,
      timestamp: new Date()?.toISOString(),
      category,
      action,
      details,
      status: status || AUDIT_STATUS?.SUCCESS,
      device: device || randomDevice?.device,
      ip: ip || randomDevice?.ip,
    };
    entries?.[uid]?.unshift(entry);
    if (entries?.[uid]?.length > 200) entries[uid] = entries?.[uid]?.slice(0, 200);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
    return entry;
  } catch {
    return null;
  }
};

export const getAuditEntries = (userId = null) => {
  try {
    const session = JSON.parse(localStorage.getItem('veiassistent_session') || 'null');
    const uid = userId || session?.id || 'unknown';
    const entries = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    return entries?.[uid] || [];
  } catch {
    return [];
  }
};

// ─── Seed demo entries ─────────────────────────────────────────────────────
export const seedDemoAuditEntries = () => {
  try {
    const session = JSON.parse(localStorage.getItem('veiassistent_session') || 'null');
    if (!session?.id) return;
    const existing = getAuditEntries();
    if (existing?.length > 0) return;
    const now = new Date();
    const ts = (offsetMinutes) => new Date(now.getTime() - offsetMinutes * 60 * 1000)?.toISOString();
    const seeds = [
      { category: AUDIT_CATEGORIES?.ACCOUNT, action: 'Konto opprettet', details: 'Ny brukerkonto registrert', timestamp: ts(60), status: AUDIT_STATUS?.SUCCESS, device: 'Chrome / Windows 11', ip: '192.168.1.42' },
      { category: AUDIT_CATEGORIES?.PROFILE, action: 'Profil oppdatert', details: 'E-postadresse bekreftet', timestamp: ts(55), status: AUDIT_STATUS?.SUCCESS, device: 'Chrome / Windows 11', ip: '192.168.1.42' },
      { category: AUDIT_CATEGORIES?.ACCOUNT, action: 'Innlogging', details: 'Vellykket innlogging fra kjent enhet', timestamp: ts(48), status: AUDIT_STATUS?.SUCCESS, device: 'Safari / macOS Ventura', ip: '10.0.0.15' },
      { category: AUDIT_CATEGORIES?.ACCOUNT, action: 'Innlogging mislyktes', details: 'Feil passord oppgitt', timestamp: ts(40), status: AUDIT_STATUS?.FAILED, device: 'Firefox / Ubuntu 22.04', ip: '172.16.0.8' },
      { category: AUDIT_CATEGORIES?.SUBSCRIPTION, action: 'Abonnement oppgradert', details: 'Oppgradert fra Basis til Premium', timestamp: ts(35), status: AUDIT_STATUS?.SUCCESS, device: 'Chrome / Windows 11', ip: '192.168.1.42' },
      { category: AUDIT_CATEGORIES?.PAYMENT, action: 'Betaling behandlet', details: 'Faktura #INV-2024-001 betalt — kr 299', timestamp: ts(30), status: AUDIT_STATUS?.SUCCESS, device: 'Chrome / Windows 11', ip: '192.168.1.42' },
      { category: AUDIT_CATEGORIES?.VEHICLE, action: 'Kjøretøy lagt til', details: 'EL12345 registrert på kontoen', timestamp: ts(25), status: AUDIT_STATUS?.SUCCESS, device: 'Chrome / Android 14', ip: '192.168.2.101' },
      { category: AUDIT_CATEGORIES?.ACCOUNT, action: 'Passord endret', details: 'Passord oppdatert via innstillinger', timestamp: ts(20), status: AUDIT_STATUS?.SUCCESS, device: 'Edge / Windows 10', ip: '10.10.5.33' },
      { category: AUDIT_CATEGORIES?.PROFILE, action: 'Adresse oppdatert', details: 'Faktureringsadresse endret', timestamp: ts(15), status: AUDIT_STATUS?.WARNING, device: 'Chrome / Windows 11', ip: '192.168.1.42' },
      { category: AUDIT_CATEGORIES?.PAYMENT, action: 'Betaling mislyktes', details: 'Korttransaksjon avvist av bank', timestamp: ts(10), status: AUDIT_STATUS?.FAILED, device: 'Safari / macOS Ventura', ip: '10.0.0.15' },
      { category: AUDIT_CATEGORIES?.ACCOUNT, action: 'To-faktor aktivert', details: '2FA aktivert via SMS', timestamp: ts(5), status: AUDIT_STATUS?.SUCCESS, device: 'Chrome / Windows 11', ip: '192.168.1.42' },
      { category: AUDIT_CATEGORIES?.ACCOUNT, action: 'Innlogging', details: 'Vellykket innlogging', timestamp: ts(1), status: AUDIT_STATUS?.SUCCESS, device: 'Chrome / Windows 11', ip: '192.168.1.42' },
    ];
    const entries = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    entries[session.id] = seeds?.map((s, i) => ({
      id: `audit-seed-${i}`,
      ...s,
    }));
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch { /* noop */ }
};

// ─── Category config ──────────────────────────────────────────────────────
const CATEGORY_CONFIG = {
  [AUDIT_CATEGORIES?.ACCOUNT]: {
    label: 'Konto',
    color: '#3B82F6',
    bg: 'rgba(59,130,246,0.12)',
    border: 'rgba(59,130,246,0.25)',
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
        <circle cx="12" cy="7" r="4"/>
      </svg>
    ),
  },
  [AUDIT_CATEGORIES?.SUBSCRIPTION]: {
    label: 'Abonnement',
    color: '#F59E0B',
    bg: 'rgba(245,158,11,0.12)',
    border: 'rgba(245,158,11,0.25)',
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
        <line x1="1" y1="10" x2="23" y2="10"/>
      </svg>
    ),
  },
  [AUDIT_CATEGORIES?.VEHICLE]: {
    label: 'Kjøretøy',
    color: '#10B981',
    bg: 'rgba(16,185,129,0.12)',
    border: 'rgba(16,185,129,0.25)',
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="1" y="3" width="15" height="13" rx="2"/>
        <path d="M16 8h4l3 3v5h-7V8z"/>
        <circle cx="5.5" cy="18.5" r="2.5"/>
        <circle cx="18.5" cy="18.5" r="2.5"/>
      </svg>
    ),
  },
  [AUDIT_CATEGORIES?.PAYMENT]: {
    label: 'Betaling',
    color: '#FF5722',
    bg: 'rgba(255,87,34,0.12)',
    border: 'rgba(255,87,34,0.25)',
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="1" x2="12" y2="23"/>
        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
      </svg>
    ),
  },
  [AUDIT_CATEGORIES?.PROFILE]: {
    label: 'Profil',
    color: '#8B5CF6',
    bg: 'rgba(139,92,246,0.12)',
    border: 'rgba(139,92,246,0.25)',
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V8z"/>
        <polyline points="14 2 14 8 20 8"/>
        <line x1="16" y1="13" x2="8" y2="13"/>
        <line x1="16" y1="17" x2="8" y2="17"/>
        <polyline points="10 9 9 9 8 9"/>
      </svg>
    ),
  },
};

// ─── Format timestamp ─────────────────────────────────────────────────────
const formatTimestamp = (iso) => {
  try {
    const d = new Date(iso);
    const date = d?.toLocaleDateString('nb-NO', { day: '2-digit', month: '2-digit', year: 'numeric' });
    const time = d?.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' });
    return { date, time, full: `${date} ${time}`, raw: d };
  } catch {
    return { date: '—', time: '—', full: '—', raw: null };
  }
};

// ─── AuditLog Component ───────────────────────────────────────────────────
const AuditLog = () => {
  const [entries, setEntries] = useState([]);
  const [activeFilter, setActiveFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    seedDemoAuditEntries();
    const delay = 800 + Math.random() * 400; // 800–1200ms
    const timer = setTimeout(() => {
      setEntries(getAuditEntries());
      setIsLoading(false);
    }, delay);
    return () => clearTimeout(timer);
  }, []);

  const activeFilterCount = [
    activeFilter !== 'all',
    statusFilter !== 'all',
    !!dateFrom,
    !!dateTo,
  ]?.filter(Boolean)?.length;

  const filtered = entries?.filter((e) => {
    const matchCat = activeFilter === 'all' || e?.category === activeFilter;
    const matchStatus = statusFilter === 'all' || e?.status === statusFilter;
    const q = searchQuery?.toLowerCase();
    const matchSearch =
      !q ||
      e?.action?.toLowerCase()?.includes(q) ||
      e?.details?.toLowerCase()?.includes(q) ||
      e?.device?.toLowerCase()?.includes(q) ||
      e?.ip?.toLowerCase()?.includes(q) ||
      CATEGORY_CONFIG?.[e?.category]?.label?.toLowerCase()?.includes(q);
    let matchDate = true;
    if (dateFrom || dateTo) {
      const ts = formatTimestamp(e?.timestamp);
      if (ts?.raw) {
        const entryDate = ts?.raw;
        if (dateFrom) {
          const from = new Date(dateFrom);
          from.setHours(0, 0, 0, 0);
          if (entryDate < from) matchDate = false;
        }
        if (dateTo) {
          const to = new Date(dateTo);
          to?.setHours(23, 59, 59, 999);
          if (entryDate > to) matchDate = false;
        }
      }
    }
    return matchCat && matchStatus && matchSearch && matchDate;
  });

  const categoryCounts = Object.values(AUDIT_CATEGORIES)?.reduce((acc, cat) => {
    acc[cat] = entries?.filter((e) => e?.category === cat)?.length;
    return acc;
  }, {});

  const resetFilters = () => {
    setActiveFilter('all');
    setStatusFilter('all');
    setDateFrom('');
    setDateTo('');
    setSearchQuery('');
  };

  const inputStyle = {
    background: 'var(--color-surface)',
    border: '1.5px solid var(--color-border)',
    borderRadius: 'var(--radius-sm)',
    fontFamily: 'var(--font-caption)',
    fontSize: '13px',
    color: 'var(--color-foreground)',
    outline: 'none',
    transition: 'border-color 200ms ease',
    padding: '8px 12px',
    width: '100%',
    boxSizing: 'border-box',
  };

  return (
    <div className="card" style={{ position: 'relative', overflow: 'hidden' }}>
      {/* Corner accents */}
      <div style={{ position: 'absolute', top: 0, right: 0, width: '60px', height: '60px', borderBottom: '2px solid rgba(255,87,34,0.15)', borderLeft: '2px solid rgba(255,87,34,0.15)', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', bottom: 0, left: 0, width: '40px', height: '40px', borderTop: '2px solid rgba(0,79,152,0.1)', borderRight: '2px solid rgba(0,79,152,0.1)', pointerEvents: 'none' }} />
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '24px', gap: '16px', flexWrap: 'wrap' }}>
        <div>
          <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold text-foreground mb-2">
            Revisjonslogg
          </h2>
          <p style={{ fontFamily: 'var(--font-caption)', fontSize: '14px', color: 'var(--color-muted-foreground)' }}>
            Kronologisk historikk over alle kontoendringer
          </p>
        </div>
        {!isLoading && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            {/* Filter toggle button */}
            <button
              onClick={() => setShowFilters(v => !v)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '7px 14px',
                background: showFilters ? 'rgba(0,79,152,0.1)' : 'transparent',
                border: showFilters ? '1.5px solid rgba(0,79,152,0.4)' : '1.5px solid var(--color-border)',
                borderRadius: 'var(--radius-sm)',
                fontFamily: 'var(--font-heading)',
                fontSize: '12px',
                fontWeight: 600,
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                color: showFilters ? 'rgba(0,79,152,0.85)' : 'var(--color-muted-foreground)',
                cursor: 'pointer',
                transition: 'all 150ms ease',
                position: 'relative',
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
              </svg>
              Filter
              {activeFilterCount > 0 && (
                <span style={{
                  position: 'absolute',
                  top: '-6px',
                  right: '-6px',
                  width: '16px',
                  height: '16px',
                  borderRadius: '50%',
                  background: 'var(--color-accent)',
                  color: '#fff',
                  fontSize: '10px',
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>{activeFilterCount}</span>
              )}
            </button>
            {/* Event count badge */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'rgba(0,79,152,0.06)', border: '1px solid rgba(0,79,152,0.12)', borderRadius: 'var(--radius-sm)', padding: '6px 14px' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(0,79,152,0.6)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12 6 12 12 16 14"/>
              </svg>
              <span style={{ fontFamily: 'var(--font-heading)', fontSize: '12px', fontWeight: 600, letterSpacing: '0.06em', color: 'rgba(0,79,152,0.7)', textTransform: 'uppercase' }}>
                {filtered?.length} / {entries?.length} hendelser
              </span>
            </div>
          </div>
        )}
      </div>
      {/* Loading skeleton */}
      {isLoading ? (
        <div>
          {/* Search skeleton */}
          <SkeletonBlock width="100%" height="44px" style={{ borderRadius: 'var(--radius-sm)', marginBottom: '16px' }} />
          {/* Category tabs skeleton */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
            {[70, 90, 80, 75, 85, 70]?.map((w, i) => (
              <SkeletonBlock key={i} width={`${w}px`} height="32px" style={{ borderRadius: 'var(--radius-sm)' }} />
            ))}
          </div>
          {/* Audit entry skeletons */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {[0, 1, 2, 3, 4, 5]?.map(i => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '14px',
                  padding: '14px 16px',
                  background: 'var(--color-muted)',
                  border: '1.5px solid var(--color-border)',
                  borderRadius: 'var(--radius-sm)',
                }}
              >
                {/* Icon placeholder */}
                <SkeletonBlock width="36px" height="36px" style={{ borderRadius: 'var(--radius-sm)', flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', flexWrap: 'wrap' }}>
                    <SkeletonBlock width={`${100 + i * 15}px`} height="14px" />
                    <SkeletonBlock width="60px" height="20px" style={{ borderRadius: 'var(--radius-sm)' }} />
                    <SkeletonBlock width="50px" height="20px" style={{ borderRadius: 'var(--radius-sm)', marginLeft: 'auto' }} />
                  </div>
                  <SkeletonBlock width={`${140 + i * 10}px`} height="12px" style={{ marginBottom: '6px' }} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <SkeletonBlock width="120px" height="11px" />
                    <SkeletonBlock width="90px" height="11px" />
                  </div>
                </div>
                <SkeletonBlock width="70px" height="12px" style={{ flexShrink: 0 }} />
              </div>
            ))}
          </div>
          {/* Spinner */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              padding: '24px 0 8px',
            }}
          >
            <LoadingSpinner size={22} color="#FF5722" />
            <span
              style={{
                fontFamily: 'var(--font-heading)',
                fontSize: '11px',
                fontWeight: 700,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: 'var(--color-muted-foreground)',
              }}
            >
              Laster aktivitetslogg...
            </span>
          </div>
        </div>
      ) : (
        <>
          {/* Search */}
          <div style={{ position: 'relative', marginBottom: '16px' }}>
            <svg
              width="16" height="16"
              viewBox="0 0 24 24" fill="none" stroke="var(--color-muted-foreground)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
            >
              <circle cx="11" cy="11" r="8"/>
              <line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              type="text"
              placeholder="Søk etter handling, enhet, IP-adresse..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              style={{
                width: '100%',
                paddingLeft: '42px',
                paddingRight: '16px',
                paddingTop: '10px',
                paddingBottom: '10px',
                background: 'var(--color-surface)',
                border: '1.5px solid var(--color-border)',
                borderRadius: 'var(--radius-sm)',
                fontFamily: 'var(--font-caption)',
                fontSize: '14px',
                color: 'var(--color-foreground)',
                outline: 'none',
                transition: 'border-color 200ms ease',
                boxSizing: 'border-box',
              }}
              onFocus={(e) => (e.target.style.borderColor = 'rgba(0,79,152,0.4)')}
              onBlur={(e) => (e.target.style.borderColor = 'var(--color-border)')}
            />
          </div>
          {/* Expandable Filters Panel */}
          {showFilters && (
            <div style={{
              background: 'rgba(0,79,152,0.04)',
              border: '1px solid rgba(0,79,152,0.12)',
              borderRadius: 'var(--radius-sm)',
              padding: '16px',
              marginBottom: '20px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
                <span style={{ fontFamily: 'var(--font-heading)', fontSize: '12px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--color-muted-foreground)' }}>
                  Filtrer resultater
                </span>
                {activeFilterCount > 0 && (
                  <button
                    onClick={resetFilters}
                    style={{
                      background: 'none',
                      border: 'none',
                      fontFamily: 'var(--font-caption)',
                      fontSize: '12px',
                      color: 'var(--color-accent)',
                      cursor: 'pointer',
                      padding: '2px 6px',
                      textDecoration: 'underline',
                    }}
                  >
                    Nullstill alle
                  </button>
                )}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
                {/* Date From */}
                <div>
                  <label style={{ display: 'block', fontFamily: 'var(--font-heading)', fontSize: '11px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--color-muted-foreground)', marginBottom: '6px' }}>
                    Fra dato
                  </label>
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e?.target?.value)}
                    style={inputStyle}
                    onFocus={(e) => (e.target.style.borderColor = 'rgba(0,79,152,0.4)')}
                    onBlur={(e) => (e.target.style.borderColor = 'var(--color-border)')}
                  />
                </div>
                {/* Date To */}
                <div>
                  <label style={{ display: 'block', fontFamily: 'var(--font-heading)', fontSize: '11px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--color-muted-foreground)', marginBottom: '6px' }}>
                    Til dato
                  </label>
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e?.target?.value)}
                    style={inputStyle}
                    onFocus={(e) => (e.target.style.borderColor = 'rgba(0,79,152,0.4)')}
                    onBlur={(e) => (e.target.style.borderColor = 'var(--color-border)')}
                  />
                </div>
                {/* Status Filter */}
                <div>
                  <label style={{ display: 'block', fontFamily: 'var(--font-heading)', fontSize: '11px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--color-muted-foreground)', marginBottom: '6px' }}>
                    Status
                  </label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e?.target?.value)}
                    style={{ ...inputStyle, cursor: 'pointer' }}
                    onFocus={(e) => (e.target.style.borderColor = 'rgba(0,79,152,0.4)')}
                    onBlur={(e) => (e.target.style.borderColor = 'var(--color-border)')}
                  >
                    <option value="all">Alle statuser</option>
                    <option value={AUDIT_STATUS?.SUCCESS}>Vellykket</option>
                    <option value={AUDIT_STATUS?.FAILED}>Mislyktes</option>
                    <option value={AUDIT_STATUS?.WARNING}>Advarsel</option>
                  </select>
                </div>
              </div>
            </div>
          )}
          {/* Category Filters */}
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '28px' }}>
            <button
              onClick={() => setActiveFilter('all')}
              style={{
                padding: '6px 14px',
                borderRadius: 'var(--radius-sm)',
                border: activeFilter === 'all' ? '1.5px solid rgba(0,79,152,0.5)' : '1.5px solid var(--color-border)',
                background: activeFilter === 'all' ? 'rgba(0,79,152,0.1)' : 'transparent',
                fontFamily: 'var(--font-heading)',
                fontSize: '12px',
                fontWeight: 600,
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                color: activeFilter === 'all' ? 'rgba(0,79,152,0.85)' : 'var(--color-muted-foreground)',
                cursor: 'pointer',
                transition: 'all 150ms ease',
              }}
            >
              Alle ({entries?.length})
            </button>
            {Object.entries(CATEGORY_CONFIG)?.map(([key, cfg]) => (
              <button
                key={key}
                onClick={() => setActiveFilter(key)}
                style={{
                  padding: '6px 14px',
                  borderRadius: 'var(--radius-sm)',
                  border: activeFilter === key ? `1.5px solid ${cfg?.border}` : '1.5px solid var(--color-border)',
                  background: activeFilter === key ? cfg?.bg : 'transparent',
                  fontFamily: 'var(--font-heading)',
                  fontSize: '12px',
                  fontWeight: 600,
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                  color: activeFilter === key ? cfg?.color : 'var(--color-muted-foreground)',
                  cursor: 'pointer',
                  transition: 'all 150ms ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <span style={{ color: activeFilter === key ? cfg?.color : 'var(--color-muted-foreground)' }}>{cfg?.icon}</span>
                {cfg?.label} ({categoryCounts?.[key] || 0})
              </button>
            ))}
          </div>
          {/* Entries */}
          {filtered?.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '56px 24px' }}>
              <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="rgba(0,79,152,0.2)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ margin: '0 auto 16px' }}>
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
                <polyline points="10 9 9 9 8 9"/>
              </svg>
              <p style={{ fontFamily: 'var(--font-heading)', fontSize: '16px', fontWeight: 600, color: 'var(--color-foreground)', marginBottom: '6px' }}>
                Ingen hendelser funnet
              </p>
              <p style={{ fontFamily: 'var(--font-caption)', fontSize: '13px', color: 'var(--color-muted-foreground)' }}>
                {searchQuery || activeFilterCount > 0 ? 'Prøv å endre filtrene' : 'Kontoaktivitet vil vises her'}
              </p>
              {activeFilterCount > 0 && (
                <button
                  onClick={resetFilters}
                  style={{
                    marginTop: '12px',
                    padding: '8px 18px',
                    background: 'none',
                    border: '1.5px solid var(--color-border)',
                    borderRadius: 'var(--radius-sm)',
                    fontFamily: 'var(--font-heading)',
                    fontSize: '12px',
                    fontWeight: 600,
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                    color: 'var(--color-muted-foreground)',
                    cursor: 'pointer',
                  }}
                >
                  Nullstill filter
                </button>
              )}
            </div>
          ) : (
            <div style={{ position: 'relative' }}>
              {/* Timeline line */}
              <div style={{ position: 'absolute', left: '23px', top: '12px', bottom: '12px', width: '2px', background: 'linear-gradient(to bottom, rgba(0,79,152,0.15), rgba(255,87,34,0.1))', borderRadius: '1px', pointerEvents: 'none' }} />

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
                {filtered?.map((entry, idx) => {
                  const cfg = CATEGORY_CONFIG?.[entry?.category] || CATEGORY_CONFIG?.[AUDIT_CATEGORIES?.ACCOUNT];
                  const statusCfg = STATUS_CONFIG?.[entry?.status] || STATUS_CONFIG?.[AUDIT_STATUS?.SUCCESS];
                  const ts = formatTimestamp(entry?.timestamp);
                  const isLast = idx === filtered?.length - 1;
                  return (
                    <div
                      key={entry?.id}
                      style={{
                        display: 'flex',
                        gap: '16px',
                        paddingBottom: isLast ? '0' : '16px',
                        position: 'relative',
                      }}
                    >
                      {/* Timeline dot */}
                      <div style={{ flexShrink: 0, width: '48px', display: 'flex', justifyContent: 'center', paddingTop: '2px' }}>
                        <div
                          style={{
                            width: '24px',
                            height: '24px',
                            borderRadius: '50%',
                            background: cfg?.bg,
                            border: `2px solid ${cfg?.border}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: cfg?.color,
                            flexShrink: 0,
                            zIndex: 1,
                            position: 'relative',
                          }}
                        >
                          {cfg?.icon}
                        </div>
                      </div>

                      {/* Content */}
                      <div
                        style={{
                          flex: 1,
                          background: 'var(--color-surface)',
                          border: '1px solid var(--color-border)',
                          borderRadius: 'var(--radius-sm)',
                          padding: '12px 16px',
                          transition: 'border-color 150ms ease',
                          minWidth: 0,
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.borderColor = cfg?.border)}
                        onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'var(--color-border)')}
                      >
                        {/* Top row: category badge + action + status + timestamp */}
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap', marginBottom: '6px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
                            {/* Category badge */}
                            <span
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                padding: '2px 8px',
                                borderRadius: '3px',
                                background: cfg?.bg,
                                border: `1px solid ${cfg?.border}`,
                                fontFamily: 'var(--font-heading)',
                                fontSize: '10px',
                                fontWeight: 700,
                                letterSpacing: '0.08em',
                                textTransform: 'uppercase',
                                color: cfg?.color,
                                flexShrink: 0,
                              }}
                            >
                              {cfg?.label}
                            </span>
                            {/* Action name */}
                            <span
                              style={{
                                fontFamily: 'var(--font-heading)',
                                fontSize: '14px',
                                fontWeight: 600,
                                color: 'var(--color-foreground)',
                              }}
                            >
                              {entry?.action}
                            </span>
                            {/* Status badge */}
                            <span
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                padding: '2px 8px',
                                borderRadius: '3px',
                                background: statusCfg?.bg,
                                border: `1px solid ${statusCfg?.border}`,
                                fontFamily: 'var(--font-heading)',
                                fontSize: '10px',
                                fontWeight: 700,
                                letterSpacing: '0.06em',
                                textTransform: 'uppercase',
                                color: statusCfg?.color,
                                flexShrink: 0,
                              }}
                            >
                              <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: statusCfg?.color, display: 'inline-block', flexShrink: 0 }} />
                              {statusCfg?.label}
                            </span>
                          </div>
                          {/* Timestamp */}
                          <div style={{ textAlign: 'right', flexShrink: 0 }}>
                            <div style={{ fontFamily: 'var(--font-heading)', fontSize: '12px', fontWeight: 600, color: 'var(--color-foreground)', letterSpacing: '0.03em' }}>
                              {ts?.time}
                            </div>
                            <div style={{ fontFamily: 'var(--font-caption)', fontSize: '11px', color: 'var(--color-muted-foreground)', marginTop: '2px' }}>
                              {ts?.date}
                            </div>
                          </div>
                        </div>

                        {/* Details */}
                        {entry?.details && (
                          <p
                            style={{
                              fontFamily: 'var(--font-caption)',
                              fontSize: '13px',
                              color: 'var(--color-muted-foreground)',
                              lineHeight: 1.5,
                              margin: '0 0 8px 0',
                            }}
                          >
                            {entry?.details}
                          </p>
                        )}

                        {/* Device / IP row */}
                        {(entry?.device || entry?.ip) && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap', paddingTop: '8px', borderTop: '1px solid var(--color-border)' }}>
                            {entry?.device && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--color-muted-foreground)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                                  <line x1="8" y1="21" x2="16" y2="21"/>
                                  <line x1="12" y1="17" x2="12" y2="21"/>
                                </svg>
                                <span style={{ fontFamily: 'var(--font-caption)', fontSize: '11px', color: 'var(--color-muted-foreground)' }}>
                                  {entry?.device}
                                </span>
                              </div>
                            )}
                            {entry?.ip && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--color-muted-foreground)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <circle cx="12" cy="12" r="10"/>
                                  <line x1="2" y1="12" x2="22" y2="12"/>
                                  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                                </svg>
                                <span style={{ fontFamily: 'var(--font-caption)', fontSize: '11px', color: 'var(--color-muted-foreground)', letterSpacing: '0.03em' }}>
                                  {entry?.ip}
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AuditLog;
